import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Subject, firstValueFrom, timer } from 'rxjs';
import { bufferTime, filter, groupBy, map, mergeMap, retry } from 'rxjs/operators';
import { updateDoc } from '@angular/fire/firestore';
import * as Sentry from '@sentry/angular';
import { AppSettingsService } from '../app-settings.service';
import { FirestoreService } from '../firestore.service';
import { SystemSettingsService } from '../system-settings.service';
import {
  ReplicationBatch,
  ReplicationBatchResponse,
  ReplicationEvent,
  ReplicationTask
} from './entity-config';

/**
 * Central event processor for the generic Data Replication pattern.
 *
 * Receives {@link ReplicationEvent}s from every {@link BaseReplicableService},
 * groups them by entity type, batches each group over a fixed time window, and
 * pushes the batch to the Desktop app via `POST /api/replication/batch`.
 *
 * Characteristics:
 * - Events are batched per entity type to minimise HTTP round-trips.
 * - Nothing is sent while the Global setting Desktop App → Data replication
 *   is Off (US 28012); the batch is dropped and no record is stamped.
 * - The Desktop upserts by `localId`, so a batch is idempotent and a given
 *   id never duplicates.
 * - On a successful push the source record is stamped so it is not
 *   re-replicated unnecessarily.
 * - The HTTP call retries with exponential backoff when the Desktop is
 *   unreachable. Processing is fire-and-forget and all failures are
 *   swallowed, so the caller is never blocked; an unreplicated record stays
 *   `UnSynced` and is picked up again by a later mutation or by recovery.
 */
@Injectable({ providedIn: 'root' })
export class ReplicationManager {
  private readonly http = inject(HttpClient);
  private readonly firestoreService = inject(FirestoreService);
  private readonly systemSettingsService = inject(SystemSettingsService);

  private readonly replicationEvents$ = new Subject<ReplicationEvent>();
  private readonly nativeServiceBaseUrl: string;

  /** Batching window, in milliseconds. */
  private static readonly BATCH_WINDOW_MS = 500;
  /** Exponential backoff retry budget when the Desktop is unreachable. */
  private static readonly MAX_RETRIES = 4;
  private static readonly BASE_BACKOFF_MS = 1000;

  constructor() {
    this.nativeServiceBaseUrl = this.normalizeBaseUrl(
      AppSettingsService.appSettings.edaraNativeService.baseUrl
    );
    this.setupEventProcessing();
  }

  /** Entry point used by {@link BaseReplicableService}. Non-blocking. */
  emitReplicationEvent(event: ReplicationEvent): void {
    this.replicationEvents$.next(event);
  }

  private setupEventProcessing(): void {
    this.replicationEvents$
      .pipe(
        groupBy(event => event.entityType),
        mergeMap(group =>
          group.pipe(
            bufferTime(ReplicationManager.BATCH_WINDOW_MS),
            filter(events => events.length > 0),
            map(events => ({ entityType: group.key, events }))
          )
        )
      )
      .subscribe(batch => {
        // Fire-and-forget: never let replication block or throw upstream.
        this.processBatch(batch).catch(error => {
          console.error(`[ReplicationManager] Batch failed for ${batch.entityType}:`, error);
          Sentry.captureException(error);
        });
      });
  }

  private async processBatch(batch: {
    entityType: string;
    events: ReplicationEvent[];
  }): Promise<void> {
    // US 28012: mirroring to the Desktop is a Global setting (Desktop App → Data
    // replication). While it is Off the batch is dropped unsent and nothing is
    // stamped — the records stay the local source of truth. The cached setting
    // is consulted per batch, so a save on this device applies to the next one.
    if (!(await this.systemSettingsService.isDataReplicationEnabled())) return;

    // Collapse to the latest event per entity id (idempotent — last write wins
    // within the window). A trailing `delete` always wins over create/update.
    const latestByEntity = new Map<string, ReplicationEvent>();
    for (const event of batch.events) {
      const id = event.entity.metadata?.localId;
      if (!id) continue;
      const existing = latestByEntity.get(id);
      if (!existing || event.operation === 'delete' || existing.timestamp <= event.timestamp) {
        latestByEntity.set(id, event);
      }
    }

    const events = Array.from(latestByEntity.values());
    if (events.length === 0) return;

    // NOTE: the payload is snapshotted HERE (at batch flush, ~500 ms after
    // emit), not at emit time. This is intentional: `event.entity` is a live
    // reference, so we send its latest state — the Desktop upsert is
    // idempotent by localId and last-write-wins, so a slightly newer snapshot
    // is correct, never stale. Do not "fix" this by cloning at emit time.
    const tasks: ReplicationTask[] = events.map(event => ({
      id: this.newId(),
      entityType: event.entityType,
      entityId: event.entity.metadata!.localId,
      operation: event.operation,
      data: event.operation === 'delete' ? null : this.toPlainObject(event.entity),
      timestamp: event.timestamp
    }));

    const payload: ReplicationBatch = { entityType: batch.entityType, tasks };

    const response = await firstValueFrom(
      this.http
        .post<ReplicationBatchResponse>(
          `${this.nativeServiceBaseUrl}/api/replication/batch`,
          payload
        )
        .pipe(
          retry({
            count: ReplicationManager.MAX_RETRIES,
            delay: (_, retryCount) =>
              timer(ReplicationManager.BASE_BACKOFF_MS * Math.pow(2, retryCount - 1))
          })
        )
    );

    await this.markReplicated(events, tasks, response);
  }

  /**
   * Stamp `metadata.replicationInfo.replicationDate` on every source record
   * whose task the Desktop accepted, so it is not re-replicated needlessly.
   * Deletes have no surviving doc to stamp. A 207 (partial) response only
   * stamps the successful task ids.
   */
  private async markReplicated(
    events: ReplicationEvent[],
    tasks: ReplicationTask[],
    response: ReplicationBatchResponse | null
  ): Promise<void> {
    const failedTaskIds = new Set(
      (response?.results ?? [])
        .filter(r => r.status === 'error')
        .map(r => r.taskId)
    );
    const replicationDate = new Date();

    await Promise.all(
      events.map(async (event, index) => {
        const task = tasks[index];
        if (event.operation === 'delete' || failedTaskIds.has(task.id)) return;
        try {
          const ref = this.firestoreService.doc(
            `${event.firestoreCollection}/${event.entity.metadata!.localId}`
          );
          await updateDoc(ref, {
            'metadata.replicationInfo.replicationDate': replicationDate,
            'metadata.replicationInfo.replicaStorageType': 'LiteDB'
          });
        } catch (error) {
          // Non-fatal: the record is safely in the Desktop; the missing
          // stamp only risks a redundant (idempotent) re-replication later.
          console.warn(
            `[ReplicationManager] Could not stamp replicationDate for ${event.entityType}/${event.entity.metadata!.localId}`,
            error
          );
        }
      })
    );
  }

  private toPlainObject(entity: any): any {
    return JSON.parse(JSON.stringify(entity));
  }

  private newId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `task_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  private normalizeBaseUrl(url: string): string {
    return url?.endsWith('/') ? url.slice(0, -1) : url;
  }
}
